import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Percent, RefreshCw, Save, Info, Clock, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoanRatesTab() {
  const [rates, setRates] = useState({ rate_15y: 0, rate_20y: 0, rate_25y: 0 });
  const [meta, setMeta] = useState({ last_updated: '', source: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = () => localStorage.getItem('token');

  const fetchRates = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/loan-rates`, { headers: { Authorization: `Bearer ${token()}` } });
      setRates({ rate_15y: r.data.rate_15y || 0, rate_20y: r.data.rate_20y || 0, rate_25y: r.data.rate_25y || 0 });
      setMeta({ last_updated: r.data.last_updated || '', source: r.data.source || '' });
    } catch {
      toast.error('Erreur chargement des taux');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRates(); }, []);

  const saveRates = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({
        rate_15y: rates.rate_15y,
        rate_20y: rates.rate_20y,
        rate_25y: rates.rate_25y,
      });
      await axios.post(`${API}/api/admin/loan-rates?${params.toString()}`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Taux enregistrés');
      await fetchRates();
    } catch {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return iso; }
  };

  const sourceLabel = {
    meilleurtaux_auto: { label: '🤖 Auto (Meilleurtaux)', color: 'emerald' },
    manual: { label: '✏️ Manuel (Admin)', color: 'amber' },
    default: { label: '⚙️ Valeurs par défaut', color: 'gray' },
  }[meta.source] || { label: meta.source || '—', color: 'gray' };

  return (
    <Card data-testid="loan-rates-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Percent className="w-6 h-6 text-indigo-700" />
          Taux de crédit immobilier
        </CardTitle>
        <p className="text-sm text-gray-600 mt-1">
          Ces taux sont utilisés par défaut dans la simulation financière du Dashboard Expert. Mis à jour automatiquement chaque semaine depuis Meilleurtaux.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 flex items-start gap-2">
          <Info className="w-5 h-5 text-indigo-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-indigo-900">
            <b>Astuce :</b> le système scrape automatiquement Meilleurtaux une fois par semaine. Si le scraping échoue, les valeurs précédentes sont conservées.
            Vous pouvez aussi saisir vos propres taux ici (ex: si votre courtier vous donne une info privilégiée).
          </div>
        </div>

        <div className="flex items-center gap-4 flex-wrap text-sm">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-500" />
            <span className="text-gray-700">Dernière MAJ :</span>
            <span className="font-mono text-navy">{formatDate(meta.last_updated)}</span>
          </div>
          <div className={`px-2 py-1 rounded-full text-xs font-semibold bg-${sourceLabel.color}-100 text-${sourceLabel.color}-700 border border-${sourceLabel.color}-300`}>
            {sourceLabel.label}
          </div>
          <Button variant="outline" size="sm" onClick={fetchRates} disabled={loading} data-testid="refresh-rates-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Recharger
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { key: 'rate_15y', label: '15 ans', hint: 'Taux court terme' },
            { key: 'rate_20y', label: '20 ans', hint: 'Taux standard (le plus utilisé)' },
            { key: 'rate_25y', label: '25 ans', hint: 'Taux long terme' },
          ].map(({ key, label, hint }) => (
            <div key={key} className="border border-gray-200 rounded-lg p-4 bg-white">
              <Label className="text-xs text-gray-500 uppercase tracking-wide">{label}</Label>
              <p className="text-[11px] text-gray-400 mb-2">{hint}</p>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.01"
                  value={rates[key]}
                  onChange={(e) => setRates({ ...rates, [key]: parseFloat(e.target.value) || 0 })}
                  className="text-2xl font-bold text-navy"
                  data-testid={`rate-${key}-input`}
                />
                <span className="text-lg text-gray-600">%</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 justify-end">
          <Button onClick={saveRates} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-rates-btn">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Enregistrement...' : 'Enregistrer les taux'}
          </Button>
        </div>

        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900">
              <b>Où ces taux apparaissent ?</b> Dans la carte <b>"💰 Simulation Financière"</b> du Dashboard Expert,
              champ <b>"Taux crédit %"</b> — pré-rempli avec le taux 20 ans que vous enregistrez ici.
              L'expert peut toujours le surcharger dossier par dossier si besoin.
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
